import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { invalidateOrganizationSettings } from '../hooks/useOrganizationSettings'
import { DC_CONFIG, getSelectedDC } from '../config'
import { apiClient } from '../utils/apiClient'
import { switchActiveOrganization } from '../utils/activeOrganization'
import { setSessionToken } from '../auth/session'

type CreateOrganizationDialogProps = {
  open: boolean
  onClose: () => void
  onCreated?: () => void
}

export default function CreateOrganizationDialog({ open, onClose, onCreated }: CreateOrganizationDialogProps) {
  const { checkAuth } = useAuth()
  const { showNotification } = useNotification()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const dcLabel = DC_CONFIG[getSelectedDC()].label

  const handleClose = () => {
    if (submitting) return
    setName('')
    onClose()
  }

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      showNotification('Organization name must be at least 2 characters', 'error')
      return
    }
    setSubmitting(true)
    try {
      const result = await apiClient.post<{
        organization_id: number
        name: string
        token?: string
      }>('/api/organizations', { name: trimmed, switch_to: true })
      if (result.token) {
        setSessionToken(result.token)
      } else if (result.organization_id) {
        await switchActiveOrganization(result.organization_id)
      }
      invalidateOrganizationSettings()
      await checkAuth()
      showNotification(`Created organization “${result.name}”`, 'success')
      setName('')
      onCreated?.()
      onClose()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create organization'
      showNotification(message, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create organization</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Start a new organization in <strong>{dcLabel}</strong> with its own projects, databases, and billing.
          You will be the owner. Names are unique within each data center — the same name can exist in Montreal or San Francisco separately.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Organization name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 64 }}
          disabled={submitting}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void handleCreate()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleCreate()} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
